import {type FilterQuery, getFilterValue, isFilterBlock, ValidationTypes} from '@github-ui/filter-core'

/**
 * Extracts org names from filter query blocks that have SSO-required validations.
 * For `repo:owner/name` filters, extracts just the owner portion.
 */
export function extractSsoOrgsFromFilterQuery(filterQuery: FilterQuery): string[] {
  const ssoOrgs = new Set<string>()
  for (const block of filterQuery.blocks) {
    if (!isFilterBlock(block)) continue
    for (const value of block.value.values) {
      if (value.validations?.some(v => v.type === ValidationTypes.SsoRequired) && value.ssoUrl) {
        const rawValue = getFilterValue(value.value) ?? ''
        // For repo filter (owner/name), extract just the owner
        const orgName = block.key.value === 'repo' && rawValue.includes('/') ? rawValue.split('/')[0]! : rawValue
        if (orgName) ssoOrgs.add(orgName)
      }
    }
  }
  return Array.from(ssoOrgs)
}

/**
 * Extracts org names referenced in a raw search query string by matching
 * `org:VALUE` and `repo:OWNER/NAME` patterns against known SSO org names.
 * This works even when the validation endpoint doesn't return SSO errors
 * (e.g. when the backend bypasses SAML enforcement for filter validation).
 */
export function extractSsoOrgsFromRawQuery(query: string, ssoOrgNames: string[]): string[] {
  if (!query || ssoOrgNames.length === 0) return []
  const ssoSet = new Set(ssoOrgNames.map(n => n.toLowerCase()))
  const matched: string[] = []

  for (const match of query.matchAll(/\borg:(\S+)/gi)) {
    const org = match[1]!
    if (ssoSet.has(org.toLowerCase())) matched.push(org)
  }
  for (const match of query.matchAll(/\brepo:(\S+?)\/\S+/gi)) {
    const owner = match[1]!
    if (ssoSet.has(owner.toLowerCase())) matched.push(owner)
  }

  return [...new Set(matched)]
}

/**
 * Filters out validation messages that are about SSO-protected orgs/repos.
 * These are generic "Invalid value X for org/repo" errors caused by the
 * validation endpoint returning 401 for SSO-enforced orgs. The SSO banner
 * replaces these messages with an actionable authentication prompt.
 */
export function filterSsoValidationMessages(messages: string[], ssoOrgNames: string[]): string[] {
  if (ssoOrgNames.length === 0) return messages
  const ssoSet = new Set(ssoOrgNames.map(n => n.toLowerCase()))
  return messages.filter(msg => {
    const match = msg.match(/Invalid value <pre>([^<]+)<\/pre> for <pre>(org|repo)<\/pre>/)
    if (!match) return true
    const value = match[1]!
    // For repo values (owner/name), check the owner portion
    const orgName = match[2] === 'repo' && value.includes('/') ? value.split('/')[0]! : value
    return !ssoSet.has(orgName.toLowerCase())
  })
}
